import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getIdea, toggleLike, categoryLabel } from "../api/ideas";
import IdeaStatusBadge from "../components/ideas/IdeaStatusBadge";
import IdeaStatusTimeline from "../components/ideas/IdeaStatusTimeline";
import IdeaCommentList from "../components/ideas/IdeaCommentList";
import IdeasTopbar from "../components/ideas/IdeasTopbar";
import { ArrowLeftIcon } from "../components/ideas/IdeasIcons";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/ideas.css";

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  );
}

/**
 * Детальная страница идеи. 2-колоночный лэйаут (на десктопе):
 *   - слева: title, описание, кнопка лайка, комменты
 *   - справа: meta + статус-timeline + ссылка-источник
 */
export default function IdeaDetailPage() {
  const { id } = useParams();
  const ideaId = parseInt(id, 10);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(false);

  usePageTitle(data ? `${data.title} — Рублокс` : "Идея — Рублокс");

  useEffect(() => {
    if (!Number.isFinite(ideaId)) return;
    const ctl = new AbortController();
    getIdea(ideaId, { signal: ctl.signal })
      .then(setData)
      .catch((e) => {
        if (e.name !== "AbortError") setErr(e.message);
      });
    return () => ctl.abort();
  }, [ideaId]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate(`/login?back=/ideas/${ideaId}`);
      return;
    }
    if (busy) return;
    setBusy(true);
    setBump(true);
    setTimeout(() => setBump(false), 420);
    const prev = { is_liked: data.is_liked, likes: data.likes };
    setData({
      ...data,
      is_liked: !data.is_liked,
      likes: data.likes + (data.is_liked ? -1 : 1),
    });
    try {
      const res = await toggleLike(ideaId);
      setData((d) => ({ ...d, is_liked: res.liked, likes: res.likes }));
    } catch (e) {
      setData((d) => ({ ...d, ...prev }));
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="idz-page">
        <IdeasTopbar />
        <main className="idz-container" style={{ paddingTop: 24 }}>
          <div className="idz-err">{err}</div>
          <p><Link to="/ideas"><ArrowLeftIcon /> К ленте идей</Link></p>
        </main>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="idz-page">
        <IdeasTopbar />
        <main className="idz-container" style={{ paddingTop: 24 }}>
          <div className="idz-loading"><span className="idz-spinner" /> Загружаю идею…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="idz-page">
      <IdeasTopbar />
      <main className="idz-container idz-detail" style={{ paddingTop: 24 }}>
        <p style={{ margin: 0 }}>
          <Link to="/ideas" style={{ color: "#3357ff", fontWeight: 600 }}>
            <ArrowLeftIcon /> К ленте идей
          </Link>
        </p>

        <div className="idz-detail-grid">
          <div>
            <div className="idz-paper">
              <h1>{data.title}</h1>
              <div className="idz-meta">
                <IdeaStatusBadge status={data.status} />
                <span>· {categoryLabel(data.category)}</span>
                <span>· {data.author_name || `автор #${data.author_id}`}</span>
                <span>· {formatDate(data.created_at)}</span>
              </div>
              <div className="idz-desc-text">{data.description}</div>

              {data.source_url && (
                <p style={{ marginTop: 16 }}>
                  <a href={data.source_url} target="_blank" rel="noreferrer"
                     className="idz-btn-secondary"
                     style={{ display: "inline-block", textDecoration: "none" }}>
                    Источник
                  </a>
                </p>
              )}

              <div className="idz-like-row">
                <button
                  className={"idz-btn-like" + (data.is_liked ? " is-liked" : "") + (bump ? " is-bumped" : "")}
                  onClick={handleLike}
                  disabled={busy}
                >
                  <HeartIcon />
                  {data.is_liked ? "Лайк поставлен" : "Поддержать"}
                  <span style={{ opacity: 0.7, marginLeft: 4 }}>· {data.likes}</span>
                </button>
                <span style={{ color: "#6b7280", fontSize: 14 }}>
                  {data.comments} комментариев
                </span>
              </div>
            </div>

            <div className="idz-paper">
              <h2 style={{ marginTop: 0, fontSize: 18 }}>Комментарии</h2>
              <IdeaCommentList ideaId={ideaId} canAct={isAuthenticated} />
            </div>
          </div>

          <aside style={{ position: "sticky", top: 70, alignSelf: "start" }}>
            <div className="idz-paper">
              <h3 style={{ marginTop: 0, fontSize: 16 }}>История статуса</h3>
              <IdeaStatusTimeline history={data.status_history} />
              {data.released_in && (
                <p style={{ marginTop: 16, color: "#10b981", fontWeight: 700 }}>
                  Выпущено в версии {data.released_in}
                </p>
              )}
              {data.decided_comment && data.status !== "voting" && (
                <p style={{ marginTop: 12, fontStyle: "italic", color: "#374151" }}>
                  Решение МИНа: «{data.decided_comment}»
                </p>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}
